import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon, PersonaIcon } from '@persona-id/design-tokens/icons';
import { startCase } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useMemo, VFC } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import FormControlPilledInput from 'dashboard/components/FormControlPilledInput';
import { Control } from 'dashboard/components/FormControlPilledInput/types';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import WorkflowObjectFilterConfigPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/WorkflowObjectFilterConfigPanel';
import WorkflowObjectFilterConfigView from 'dashboard/components/WorkflowControlPanel/StepConfigAction/WorkflowObjectFilterView';
import StepConfigTypeCard from 'dashboard/components/WorkflowControlPanel/StepConfigTypeCard';
import TriggerAdvancedConfigurationPanel from 'dashboard/components/WorkflowControlPanel/TriggerAdvancedConfigurationPanel/TriggerAdvancedConfigurationPanel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { DateTimeValue, ValueType } from 'dashboard/models/graph-query';
import {
  TriggerObjectType,
  TriggerObjectTypes,
  TriggerRecurring,
  TriggerRecurringAttributes,
} from 'dashboard/models/trigger';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import { showMessage } from 'dashboard/redux/actions/messages';
import Banner from 'design-system/components/Banner';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { headingMixins } from 'design-system/mixins/text';
import useApiLoader from 'lib/hooks/useApiLoader';
import useToggle from 'lib/hooks/useToggle';

const Well = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]} ${Spacing[4]};
`;

const StyledLabel = styled(Label)`
  padding: ${Spacing[2]} 0;
`;

const InputFieldsHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const SubTitle = styled.div`
  ${headingMixins.sm}
  padding-bottom: ${Spacing[2]};
`;

const SCHEDULE_OPTIONS = [
  {
    label: 'Every Minute',
    value: 'FREQ=TEST;',
  },
  {
    label: 'Every Hour',
    value: 'FREQ=HOURLY;',
  },
  {
    label: 'Every Day',
    value: 'FREQ=DAILY;',
  },
  {
    label: 'Every Week',
    value: 'FREQ=WEEKLY;',
  },
  {
    label: 'Every Month',
    value: 'FREQ=MONTHLY;',
  },
  {
    label: 'Every Year',
    value: 'FREQ=YEARLY;',
  },
];

const SCHEDULES_WITHOUT_START = new Set(['FREQ=TEST;', 'FREQ=HOURLY;']);

interface TriggerConfigRecurringProps {
  trigger: TriggerRecurring;
  editable: boolean;
  errors: WorkflowError[];
  workflowSchema: WorkflowSchema;
  handleClose: () => void;
  updateTrigger: (newTrigger: TriggerRecurring) => void;
  workflowVersionExpiration: number;
  updateWorkflowVersionExpiration: (expiration: number) => void;
  loadFilterFieldConfigs?: () => Promise<void>;
}

const TriggerConfigRecurring: VFC<TriggerConfigRecurringProps> = ({
  trigger,
  editable,
  errors,
  workflowSchema,
  handleClose,
  updateTrigger,
  workflowVersionExpiration,
  updateWorkflowVersionExpiration,
  loadFilterFieldConfigs,
}) => {
  const configs = useMemo(() => {
    return (
      workflowSchema['filter-field-configs'][
        trigger.attributes['trigger-object-type'] as TriggerObjectType
      ] ?? []
    );
  }, [trigger, workflowSchema]);

  const { loading: isLoading } = useApiLoader({
    loader: async () => loadFilterFieldConfigs?.(),
    deps: [],
    skip: !loadFilterFieldConfigs || configs.length > 0,
    onError: () => {
      showMessage('Some information on recurring trigger failed to load.', MessageType.Notice);
    },
    onNetworkError: () => {
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure);
    },
  });

  const triggerObjectTypeOptions = useMemo(
    () =>
      TriggerObjectTypes.map((one: TriggerObjectType) => ({
        value: one,
        label: startCase(one),
      })),
    [TriggerObjectTypes],
  );

  const updateTriggerRecurringObjectType = useCallback(
    (newType: TriggerObjectType) => {
      updateTrigger({
        ...trigger,
        attributes: {
          ...trigger.attributes,
          'trigger-object-type': newType,
          filter:
            newType !== trigger.attributes['trigger-object-type']
              ? []
              : trigger.attributes['filter'],
        },
      });
    },
    [trigger, updateTrigger],
  );

  const updateTriggerRecurringRRule = useCallback(
    (rrule: string) => {
      // Automatically set start times for these frequencies
      const startAt = SCHEDULES_WITHOUT_START.has(rrule)
        ? new Date().toISOString().split('T')[0]
        : trigger.attributes['start-at'];

      updateTrigger({
        ...trigger,
        attributes: {
          ...trigger.attributes,
          rrule: rrule,
          'start-at': startAt,
        },
      });
    },
    [trigger, updateTrigger],
  );

  const [
    showFilterFieldsPanel,
    { toggle: toggleFilterFieldsPanel, disable: closeFilterFieldsPanel },
  ] = useToggle(false);

  const updateTriggerParams = <T extends 'start-at' | 'filter'>(
    key: T,
    newValue: TriggerRecurringAttributes[T],
  ) => {
    updateTrigger({
      ...trigger,
      attributes: {
        ...trigger.attributes,
        [key]: newValue,
      },
    });
  };

  const startAtValue = useMemo(() => {
    return trigger.attributes['start-at']
      ? [
          {
            type: ValueType.DateTime,
            moment: DateTime.fromISO(trigger.attributes['start-at']).toUTC().toISO(),
            'raw-value': trigger.attributes['start-at'],
            value: trigger.attributes['start-at'],
          },
        ]
      : [];
  }, [trigger.attributes]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof TriggerRecurring['attributes']>(errors);

  return (
    <>
      <Spacer height={2} />
      <StepConfigTypeCard
        icon={<PersonaIcon size={24} />}
        title='Recurring Trigger'
        description='Trigger on recurring schedule'
      />
      <Spacer height={2} />
      <SubTitle>Configuration</SubTitle>
      <Banner style={{ padding: Spacing[3] }} variant='warning'>
        Recurring workflows can quickly make irreversible changes to large volumes of Persona data!
        Please look both ways before activating.
      </Banner>
      <Spacer height={2} />
      <ParamLabel htmlFor='trigger-config-recurring__object-type-rrule' labelText='Schedule' />
      <Select
        disabled={!editable}
        hasError={hasErrorFor('rrule')}
        id='trigger-config-recurring__object-type-rrule'
        value={trigger.attributes.rrule}
        onValueChange={updateTriggerRecurringRRule}
      >
        {SCHEDULE_OPTIONS.map(({ label, value }) => (
          <Option key={value} value={value}>
            {label}
          </Option>
        ))}
      </Select>
      {trigger.attributes['rrule'] === 'FREQ=TEST;' && (
        <>
          <Spacer height={1} />
          <Banner style={{ padding: Spacing[3] }}>
            For DEVELOPMENT/TEST only! This will trigger every minute for 1 hour!
          </Banner>
        </>
      )}
      {renderErrorMessageFor('rrule')}
      {!SCHEDULES_WITHOUT_START.has(trigger.attributes['rrule']) && (
        <>
          <Spacer height={2} />
          <ParamLabel labelText='Start date/time' />
          <FormControlPilledInput
            isCompact
            controlConfig={{
              multi: false,
              type: Control.DateTimePicker,
            }}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onChange={(value: DateTimeValue[]) => {
              updateTriggerParams('start-at', value[0]?.['raw-value']);
            }}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            value={startAtValue}
            hasError={false}
            placeholder=''
          />
        </>
      )}
      <Spacer height={2} />
      <ParamLabel htmlFor='trigger-config-recurring__object-type-select' labelText='Object Type' />
      <Select
        disabled={!editable}
        hasError={hasErrorFor('trigger-object-type')}
        id='trigger-config-recurring__object-type-select'
        value={trigger.attributes['trigger-object-type']}
        onValueChange={updateTriggerRecurringObjectType}
      >
        {triggerObjectTypeOptions.map(({ label, value }) => (
          <Option key={value} value={value}>
            {label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('trigger-object-type')}
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <Spacer height={2} />
          <InputFieldsHeading>
            <ParamLabel
              htmlFor='trigger-config-recurring__object-type-filter'
              labelText='Filter Criteria'
            />

            <HStack>
              <Button
                id='trigger-config-recurring__object-type-filter'
                type='button'
                variant='basic'
                onClick={toggleFilterFieldsPanel}
              >
                <EditIcon />
              </Button>
            </HStack>
          </InputFieldsHeading>
        </>
      )}
      {renderErrorMessageFor('filter')}
      <Spacer height={2} />
      {trigger.attributes['filter']?.length > 0 && (
        <Well>
          <StyledLabel size='sm'>Where</StyledLabel>
          <WorkflowObjectFilterConfigView
            availableConfigs={configs}
            filters={trigger.attributes['filter']}
          />
        </Well>
      )}
      {showFilterFieldsPanel && (
        <WorkflowObjectFilterConfigPanel
          editable={editable}
          filterConfigs={configs}
          filterCriteria={trigger.attributes['filter'] || []}
          idPrefix='trigger-config-recurring'
          title='Filter Criteria'
          onOutsideClick={closeFilterFieldsPanel}
          updateFilterCriteria={(t) => updateTriggerParams('filter', t)}
        />
      )}
      <Spacer height={1} />
      <TriggerAdvancedConfigurationPanel
        editable={editable}
        workflowVersionExpiration={workflowVersionExpiration}
        updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
      />
      <Spacer height={3} />
      <HStack equal={true}>
        <Button
          aria-label='Close panel'
          id='trigger-config-recurring__close-button'
          onClick={handleClose}
        >
          Close
        </Button>
      </HStack>
    </>
  );
};

export default TriggerConfigRecurring;
